<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Scorm File</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of Scorm File</div>
      <div>You can add and delete all of Scorm File from this page.</div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-container">
          <input
            type="text"
            class="admin-search-input"
            placeholder="Search..."
            v-model="searchQuery"
            @input="handleSearchChange"
          />
          <img
            src="/images/maker-cart/union.svg"
            alt="Search"
            class="admin-search-icon"
          />
        </div>
        <BaseButton
          variant="secondary"
          class="mx-2 my-md-auto"
          @click="handleAction({ type: 'add' })"
        >
          <div class="d-flex align-center ga-1">
            <img
              src="/images/icon/plus-circle.svg"
              width="20"
              height="20"
              alt="download-icon"
            />
            <span class="admin-button-text">Add Scorm File</span>
          </div>
        </BaseButton>
      </div>
      <div class="admin-list-tool-container"></div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.last_page || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="true"
        :is-view-action="true"
        :is-edit-action="false"
        :is-delete-action="true"
        @action="handleAction"
        :use-api-sort="false"
        :customActions="[]"
        variant="rounded"
      />
    </div>
  </div>

  <ConfirmationModal
    v-model="showModalDelete"
    variant="danger"
    title="Remove Scorm File"
    message="Are you sure you want to remove this Scorm File?"
    confirm-text="Confirm"
    cancel-text="Back"
    :loading="isLoading"
    @confirm="remove(itemId)"
    @cancel="updateVisibilityModalDelete"
  />

  <UploadScormModal
    v-model="showUploadModal"
    @save="handleScormUploaded"
    @cancel="showUploadModal = false"
  />
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, watch, inject } from "vue";
import { useRouter } from "vue-router";
import ScormFilesService from "@/services/scormFilesService";
import { debounce } from "lodash";
import TableData from "@/components/common/TableData.vue";
import BaseButton from "@/components/common/BaseButton.vue";
import ConfirmationModal from "@/components/common/ConfirmationModal.vue";
import UploadScormModal from "@/components/admin/scorm-files/UploadScormModal.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";

const showModalDelete = ref(false);
const showUploadModal = ref(false);
const isLoading = ref(false);
const itemId = ref(null);
const tableLoading = ref(true);
const searchQuery = ref("");
const notify = inject("notify");

const data = ref({
  current_page: 1,
  data: [],
  last_page: 0,
  total: 0,
});

const currentPage = ref(1);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
});

// Setup URL query sync with filters
useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

onMounted(async () => {
  await getAll();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

// Define table headers
const headers = ref([
  { text: "Date", value: "created_at", sortable: true },
  { text: "Title", value: "title", sortable: true },
]);

// Map API data to table items
const tableItems = computed(() => {
  return (data.value?.data || []).map((item) => ({
    id: item.uuid,
    created_at: item.created_at || "-",
    title: item.title || "-",
  }));
});

const getAll = async () => {
  try {
    tableLoading.value = true;

    const param = { page: filters.value.page, search: filters.value.search };

    const response = await ScormFilesService.getAllWithPagination(param);
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.log(error.message);
  } finally {
    tableLoading.value = false;
  }
};

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Debounced search handler
const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

const openModal = (id) => {
  itemId.value = id;
  showModalDelete.value = true;
};

const updateVisibilityModalDelete = () => {
  showModalDelete.value = !showModalDelete.value;
};

const remove = async (id) => {
  isLoading.value = true;
  try {
    await ScormFilesService.remove(id);
    showModalDelete.value = false;
    await getAll();
    notify && notify.success("Scorm File deleted successfully", "Success");
  } catch (error) {
    console.error(error.message);
    notify && notify.error("Failed to delete Scorm File", "Error");
  } finally {
    isLoading.value = false;
  }
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    // Memindahkan toolContainer di bawah admin-flex-container
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    // Memindahkan toolContainer kembali ke dalam admin-flex-container
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

// Handle actions from TableData
const router = useRouter();
const handleAction = (action) => {
  if (action.type === "view") {
    const uuid = action.item.id;
    const link = `https://scorm.eddy4teachers.com/scorm/${uuid}/910`;
    window.open(link, "_blank");
  } else if (action.type === "delete") {
    openModal(action.item.id);
  } else if (action.type === "add") {
    showUploadModal.value = true;
  }
};

const handleScormUploaded = async () => {
  await getAll();
  notify && notify.success("SCORM package uploaded successfully!", "Success");
};
</script>
